import { SortValues } from '@omniafishing/core';
import { Select } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryString } from '../../hooks/use_query_string';
import { OmniaQueryParams } from '../../lib/query_string';
import { getIpState } from '../../redux/geographic_location';
import { getStates } from '../../redux/reference_data';
import styles from './sort_results.less';

export const sortValues: Record<SortValues, SortValues> = {
  [SortValues.NEWEST]: SortValues.NEWEST,
  [SortValues.PRICE_ASC]: SortValues.PRICE_ASC,
  [SortValues.PRICE_DESC]: SortValues.PRICE_DESC,
  [SortValues.BESTSELLING]: SortValues.BESTSELLING,
  [SortValues.BEST_MATCH]: SortValues.BEST_MATCH,
  [SortValues.BRAND_ASC]: SortValues.BRAND_ASC,
  [SortValues.BRAND_DESC]: SortValues.BRAND_DESC,
  [SortValues.STATE_POPULARITY]: SortValues.STATE_POPULARITY,
};

export function getSortValue(value: string, fallback?: SortValues) {
  return sortValues[value as SortValues] || fallback || SortValues.BESTSELLING;
}

interface SortResultsProps {
  onChange?: (val: SortValues) => void;
  defaultSortValue?: SortValues;
  setQueryString: boolean;
  className?: string;
  bordered?: boolean;
}

// Currently this component gets its value from the query string, which would limit it to 1 per page
// We may need to add a way to do a value prop to control externally too
export const SortResults = (props: SortResultsProps) => {
  const { onChange, defaultSortValue, setQueryString, className, bordered = false } = props;

  const { replaceQueryString, getCurrentQuery } = useQueryString();
  const currentQuery = getCurrentQuery<{
    [OmniaQueryParams.sort]: string;
  }>();
  const defaultSort = defaultSortValue || null;
  const querySortValue = currentQuery.sort;
  const [sortValue, setSortValue] = useState(getSortValue(querySortValue || defaultSort) as string);
  const ipState = useSelector(getIpState);
  const states = useSelector(getStates);

  useEffect(() => {
    if (querySortValue) {
      if (querySortValue.includes(SortValues.STATE_POPULARITY)) {
        const stateAbbrev = querySortValue.split('_')[2];
        if (stateAbbrev && states.some((s) => s.abbr.toLowerCase() === stateAbbrev.toLowerCase())) {
          if (querySortValue !== sortValue) {
            setSortValue(querySortValue);
          }
        }
      } else {
        const sortVal = getSortValue(querySortValue);
        if (sortVal !== sortValue) {
          setSortValue(sortVal);
        }
      }
    }
  }, [querySortValue]);

  const handleChange = (val: SortValues) => {
    if (setQueryString) {
      replaceQueryString({ [OmniaQueryParams.sort]: val });
    }

    onChange?.(val);
  };

  return (
    <div className={classNames(styles.sortResults, className)}>
      <span className={styles.text}>Sort By:</span>
      <Select
        value={sortValue}
        onChange={handleChange}
        className={styles.select}
        popupClassName={styles.dropdown}
        dropdownMatchSelectWidth={false}
        bordered={bordered}
      >
        <Select.Option value={SortValues.NEWEST}>Newest</Select.Option>
        <Select.Option value={SortValues.BEST_MATCH}>Best Match</Select.Option>
        <Select.Option value={SortValues.BESTSELLING}>Best Selling</Select.Option>
        {ipState && (
          <Select.Option value={`${SortValues.STATE_POPULARITY}_${ipState}`}>
            Popular In {ipState}
          </Select.Option>
        )}
        <Select.Option value={SortValues.PRICE_ASC}>Price: Low to High</Select.Option>
        <Select.Option value={SortValues.PRICE_DESC}>Price: High to Low</Select.Option>
        <Select.Option value={SortValues.BRAND_ASC}>Brand: A to Z</Select.Option>
        <Select.Option value={SortValues.BRAND_DESC}>Brand: Z to A</Select.Option>
      </Select>
    </div>
  );
};
